import { ChevronLeft, ChevronRight, Heart, Trash2 } from "lucide-react";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import NotesConfirmModal from "./NotesConfirmModal";
import LibraryHighlightsView from "./LibraryHighlightsView";
import PackAssignDropdown from "./PackAssignDropdown";
import ToggleSwitch from "./ToggleSwitch";
import { normalizeRegisterSearch } from "../utils/registerWordListDisplay";
import {
  createLibraryHighlight,
  deleteLibraryBook,
  deleteLibraryHighlight,
  fetchLibraryBooks,
  fetchLibraryHighlights,
  fetchLibraryReading,
  libraryCoverUrl,
} from "../utils/libraryApi";
import { applyHighlightsToPages, readSelection, selectionTouchesMark } from "../utils/libraryHighlightDom";
import LibraryCharacters from "./LibraryCharacters";
import LibrarySearch from "./LibrarySearch";
import { isPartialWordQuote } from "../utils/libraryHighlightText";
import {
  readLibraryFavorites,
  readLibraryPlace,
  latestInProgressBookId,
  toggleLibraryFavorite,
  writeLibraryPlace,
} from "../utils/libraryReading";
import "./LibraryView.css";

function sniffedImageType(bytes) {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "image/png";
  }
  if (bytes.length >= 6) {
    const gif = String.fromCharCode(...bytes.slice(0, 6));
    if (gif === "GIF87a" || gif === "GIF89a") return "image/gif";
  }
  if (
    bytes.length >= 12
    && String.fromCharCode(...bytes.slice(0, 4)) === "RIFF"
    && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP"
  ) {
    return "image/webp";
  }
  const head = new TextDecoder().decode(bytes.slice(0, 180)).trim().toLowerCase();
  if (head.startsWith("<svg") || (head.startsWith("<?xml") && head.includes("<svg"))) return "image/svg+xml";
  return "";
}

const coverCache = new Map();
const coverInflight = new Map();

function loadCover(bookId) {
  const cached = coverCache.get(bookId);
  if (cached) return Promise.resolve(cached);
  const pending = coverInflight.get(bookId);
  if (pending) return pending;
  const request = fetch(libraryCoverUrl(bookId), { credentials: "include" })
    .then((res) => (res.ok ? res.blob() : null))
    .then(async (blob) => {
      if (!blob) return "";
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const type = sniffedImageType(bytes) || (blob.type.startsWith("image/") ? blob.type.split(";")[0] : "");
      if (!type) return "";
      const url = URL.createObjectURL(new Blob([bytes], { type }));
      coverCache.set(bookId, url);
      return url;
    })
    .catch(() => "")
    .finally(() => {
      coverInflight.delete(bookId);
    });
  coverInflight.set(bookId, request);
  return request;
}

function forgetCover(bookId) {
  const url = coverCache.get(bookId);
  if (url) URL.revokeObjectURL(url);
  coverCache.delete(bookId);
}

function BookCover({ bookId, hasCover }) {
  const [src, setSrc] = useState(() => (hasCover && bookId ? coverCache.get(bookId) || "" : ""));

  useEffect(() => {
    if (!hasCover || !bookId) return undefined;
    const cached = coverCache.get(bookId);
    if (cached) {
      setSrc(cached);
      return undefined;
    }
    let cancelled = false;
    loadCover(bookId).then((url) => {
      if (!cancelled && url) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [bookId, hasCover]);

  return src ? <img src={src} alt="" /> : null;
}

function bookMatches(book, query, scope) {
  if (!query) return true;
  const field = scope === "author" ? book.author : book.title;
  return normalizeRegisterSearch(field).includes(query);
}

const LIBRARY_STATUS_OPTIONS = [
  { value: "all", label: "tous les livres" },
  { value: "reading", label: "en cours de lecture" },
  { value: "done", label: "terminés" },
  { value: "unopened", label: "jamais commencés" },
];

function readingStatus(bookId) {
  const percent = readLibraryPlace(bookId)?.percent || 0;
  if (percent >= 100) return "done";
  if (percent > 0) return "reading";
  return "unopened";
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

let epubStyleNode = null;

function takeEpubCss(html) {
  const seen = new Set();
  const styles = [];
  const stripped = String(html || "").replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_, css) => {
    const text = String(css || "").trim();
    if (text && !seen.has(text)) {
      seen.add(text);
      styles.push(text);
    }
    return "";
  });
  return { html: stripped, css: styles.join("\n") };
}

function mountEpubCss(css) {
  if (typeof document === "undefined") return;
  if (!epubStyleNode) {
    epubStyleNode = document.createElement("style");
    epubStyleNode.setAttribute("data-library-epub", "1");
    document.head.append(epubStyleNode);
  }
  epubStyleNode.textContent = css || "";
}

const COLUMN_GAP = 48;
const PAGE_NUMBER_BAND = 28;

const PAGE_COUNT_STORAGE = "vocabularium-library-page-counts";
const pageCountCache = new Map();

function chapterCountKey(bookId, fontSize, width, height, index) {
  return `v2|${bookId}|${fontSize}|${Math.round(width)}|${Math.round(height)}|${index}`;
}

function readStoredPageCount(key) {
  const memory = pageCountCache.get(key);
  if (memory) return memory;
  try {
    const stored = JSON.parse(localStorage.getItem(PAGE_COUNT_STORAGE) || "{}");
    const count = stored[key];
    if (Number.isInteger(count) && count > 0) {
      pageCountCache.set(key, count);
      return count;
    }
  } catch {
    /* le nombre sera recalculé une fois */
  }
  return 0;
}

function writeStoredPageCount(key, count) {
  pageCountCache.set(key, count);
  try {
    const stored = JSON.parse(localStorage.getItem(PAGE_COUNT_STORAGE) || "{}");
    stored[key] = count;
    const keys = Object.keys(stored);
    while (Object.keys(stored).length > 240) delete stored[Object.keys(stored)[0]];
    localStorage.setItem(PAGE_COUNT_STORAGE, JSON.stringify(stored));
  } catch {
    /* la mémoire de l'onglet suffit */
  }
}

function foldReadingChar(char) {
  if (char === "\u2019" || char === "\u2018" || char === "\u02bc" || char === "\u00b4" || char === "`") return "'";
  if (char === "\u201c" || char === "\u201d" || char === "\u00ab" || char === "\u00bb") return '"';
  if (char === "\u2013" || char === "\u2014") return "-";
  return char;
}

function foldReadingText(value) {
  return String(value || "")
    .replace(/[\u2019\u2018\u02bc\u00b4`]/g, "'")
    .replace(/[\u201c\u201d\u00ab\u00bb]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

const READING_BLOCK = "p, div, li, h1, h2, h3, h4, h5, h6, blockquote, section, figcaption, td, th";

function collapsedPoint(root, needle) {
  const wanted = foldReadingText(needle);
  if (wanted.length < 8) return null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const segments = [];
  let built = "";
  let previousBlock = null;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const block = node.parentElement?.closest(READING_BLOCK) || null;
    if (previousBlock && block && block !== previousBlock && built && !built.endsWith(" ")) {
      segments.push({ node, offset: 0, text: " " });
      built += " ";
    }
    if (block) previousBlock = block;
    const value = node.nodeValue || "";
    let buffer = "";
    let startOffset = 0;
    const flush = () => {
      if (!buffer) return;
      segments.push({ node, offset: startOffset, text: buffer });
      built += buffer;
      buffer = "";
    };
    for (let index = 0; index < value.length; index += 1) {
      if (/\s/.test(value[index])) {
        flush();
        if (built && !built.endsWith(" ")) {
          segments.push({ node, offset: index, text: " " });
          built += " ";
        }
      } else {
        if (!buffer) startOffset = index;
        buffer += foldReadingChar(value[index]);
      }
    }
    flush();
  }
  const at = built.indexOf(wanted);
  if (at < 0) return null;
  let cursor = 0;
  for (const segment of segments) {
    if (cursor + segment.text.length > at) {
      const offset = segment.offset + (at - cursor);
      const limit = segment.node.nodeValue?.length || 0;
      return { node: segment.node, offset: Math.max(0, Math.min(offset, limit)) };
    }
    cursor += segment.text.length;
  }
  return null;
}

function columnStride(columnWidth) {
  return columnWidth + COLUMN_GAP;
}

function pageIndexAt(flow, node, offset, columnWidth) {
  const range = document.createRange();
  const limit = node.nodeValue?.length || 0;
  const start = Math.max(0, Math.min(offset, limit));
  const sample = Math.min(limit, start + 24);
  range.setStart(node, start);
  if (sample > start) range.setEnd(node, sample);
  else range.collapse(true);
  const rects = [...range.getClientRects()].filter((rect) => rect.width > 0.5 && rect.height > 0.5);
  if (!rects.length) return 0;
  const base = flow.getBoundingClientRect().left;
  const stride = columnStride(columnWidth);
  const columnOf = (rect) => {
    const mid = rect.left + rect.width / 2 - base;
    const index = Math.floor(Math.max(0, mid) / stride);
    const into = mid - index * stride;
    return into > columnWidth ? index + 1 : index;
  };
  const lineTop = rects[0].top;
  const sameLine = rects.filter((rect) => Math.abs(rect.top - lineTop) < 3);
  return Math.max(...sameLine.map(columnOf));
}

function pageCountFromFlow(flow, columnWidth) {
  const stride = columnStride(columnWidth);
  const width = flow.scrollWidth;
  if (width > flow.clientWidth + stride * 0.5) {
    return Math.max(1, Math.round((width + COLUMN_GAP) / stride));
  }
  const range = document.createRange();
  range.selectNodeContents(flow);
  const rects = range.getClientRects();
  const base = flow.getBoundingClientRect().left;
  let maxLeft = base;
  for (const rect of rects) {
    if (rect.width < 0.5 && rect.height < 0.5) continue;
    if (rect.left > maxLeft) maxLeft = rect.left;
  }
  return Math.max(1, Math.floor((maxLeft - base + 1) / stride) + 1);
}

function anchorLineRect(flow, pos) {
  const limit = pos.node?.nodeValue?.length || 0;
  const start = Math.max(0, Math.min(pos.offset, limit));
  let endNode = pos.node;
  let endOffset = start;
  let remaining = 24;
  const walker = document.createTreeWalker(flow, NodeFilter.SHOW_TEXT);
  walker.currentNode = pos.node;
  let node = pos.node;
  while (node && remaining > 0) {
    const value = node.nodeValue || "";
    const from = node === pos.node ? start : 0;
    let index = from;
    for (; index < value.length && remaining > 0; index += 1) {
      if (!/\s/.test(value[index])) remaining -= 1;
    }
    endNode = node;
    endOffset = index;
    if (remaining <= 0) break;
    node = walker.nextNode();
  }
  const range = document.createRange();
  range.setStart(pos.node, start);
  if (endNode && (endNode !== pos.node || endOffset > start)) range.setEnd(endNode, endOffset);
  else range.collapse(true);
  const rects = [...range.getClientRects()].filter((rect) => rect.width > 0.5 && rect.height > 0.5);
  if (!rects.length) return null;
  const lineTop = rects[0].top;
  const sameLine = rects.filter((rect) => Math.abs(rect.top - lineTop) < 3);
  return sameLine.reduce((best, rect) => (rect.right > best.right ? rect : best));
}

function quoteSpreadDelta(rect, frame) {
  if (!rect || !frame || frame.width <= 0) return null;
  if (rect.left >= frame.right - 1) return 1;
  if (rect.right <= frame.left + 1) return -1;
  return 0;
}

function alignSpreadToQuote(shift, columns, flow, pos, guess, spreads, stride) {
  const visit = (spread) => {
    shift.style.transform = `translateX(${-spread * 2 * stride}px)`;
    return quoteSpreadDelta(anchorLineRect(flow, pos), columns.getBoundingClientRect());
  };
  const clamped = Math.min(spreads - 1, Math.max(0, guess));
  const first = visit(clamped);
  if (first == null || first === 0) return clamped;
  let low = first > 0 ? clamped + 1 : 0;
  let high = first > 0 ? spreads - 1 : clamped - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const delta = visit(mid);
    if (delta == null) return clamped;
    if (delta === 0) return mid;
    if (delta > 0) low = mid + 1;
    else high = mid - 1;
  }
  return clamped;
}

function spreadForAnchor(flow, anchor, columnWidth, pageCount, columns, shift) {
  const folded = foldReadingText(anchor);
  const lengths = [folded.length, 80, 40].filter((length, index, list) => length >= 12 && list.indexOf(length) === index);
  let pos = null;
  for (const length of lengths) {
    pos = collapsedPoint(flow, folded.slice(0, length));
    if (pos) break;
  }
  if (!pos) return null;
  const page = pageIndexAt(flow, pos.node, pos.offset, columnWidth);
  const spreads = Math.max(1, Math.ceil(pageCount / 2));
  const guess = Math.min(spreads - 1, Math.floor(page / 2));
  if (!columns || !shift || spreads <= 1) return guess;
  return alignSpreadToQuote(shift, columns, flow, pos, guess, spreads, columnStride(columnWidth));
}

const PLACE_ANCHOR_LENGTH = 120;

function firstIndexReaching(count, pageAt, target) {
  const seek = (from, to) => {
    for (let index = from; index < to; index += 1) {
      const page = pageAt(index);
      if (page != null) return { index, page };
    }
    return null;
  };
  let low = 0;
  let high = count;
  while (low < high) {
    const mid = (low + high) >> 1;
    const hit = seek(mid, high);
    if (!hit || hit.page >= target) high = mid;
    else low = hit.index + 1;
  }
  return seek(low, count);
}

function charPageIndex(node, offset, base, columnWidth) {
  const value = node.nodeValue || "";
  const code = value.charCodeAt(offset);
  if (!(code >= 0) || /\s/.test(value[offset]) || (code >= 0xdc00 && code <= 0xdfff)) return null;
  const range = document.createRange();
  range.setStart(node, offset);
  range.setEnd(node, Math.min(value.length, offset + (code >= 0xd800 && code <= 0xdbff ? 2 : 1)));
  const rect = [...range.getClientRects()].find((item) => item.width > 0.5 || item.height > 0.5);
  if (!rect) return null;
  const stride = columnStride(columnWidth);
  const mid = rect.left + rect.width / 2 - base;
  const index = Math.floor(Math.max(0, mid) / stride);
  return mid - index * stride > columnWidth ? index + 1 : index;
}

function lastInkPageIndex(node, base, columnWidth) {
  const value = node.nodeValue || "";
  for (let offset = value.length - 1; offset >= 0; offset -= 1) {
    if (/\s/.test(value[offset])) continue;
    const page = charPageIndex(node, offset, base, columnWidth);
    if (page != null) return page;
    if (value.length - offset > 8) return null;
  }
  return null;
}

/** Texte replié comme dans collapsedPoint, à partir d'un point du flux. */
function collapsedTextFrom(flow, node, offset) {
  const walker = document.createTreeWalker(flow, NodeFilter.SHOW_TEXT);
  walker.currentNode = node;
  let built = "";
  let previousBlock = null;
  let current = node;
  let from = offset;
  while (current && built.length < PLACE_ANCHOR_LENGTH) {
    const block = current.parentElement?.closest(READING_BLOCK) || null;
    if (previousBlock && block && block !== previousBlock && built && !built.endsWith(" ")) built += " ";
    if (block) previousBlock = block;
    const value = current.nodeValue || "";
    for (let index = from; index < value.length && built.length < PLACE_ANCHOR_LENGTH; index += 1) {
      if (/\s/.test(value[index])) {
        if (built && !built.endsWith(" ")) built += " ";
      } else {
        built += value[index];
      }
    }
    from = 0;
    current = walker.nextNode();
  }
  return built.trim();
}

/** Ancre du premier texte visible à partir de la page `page` du chapitre affiché. */
function pageAnchor(flow, page, columnWidth) {
  if (!flow || !(columnWidth > 0)) return "";
  const base = flow.getBoundingClientRect().left;
  const nodes = [];
  const walker = document.createTreeWalker(flow, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    if (/\S/.test(walker.currentNode.nodeValue || "")) nodes.push(walker.currentNode);
  }
  const nodeHit = firstIndexReaching(
    nodes.length,
    (index) => lastInkPageIndex(nodes[index], base, columnWidth),
    page
  );
  if (!nodeHit) return "";
  const node = nodes[nodeHit.index];
  const charHit = firstIndexReaching(
    (node.nodeValue || "").length,
    (offset) => charPageIndex(node, offset, base, columnWidth),
    page
  );
  return charHit ? collapsedTextFrom(flow, node, charHit.index) : "";
}

function chapterMarkup(chapter) {
  return `<section class="library-spread__chapter"><h2 class="library-spread__chapter-title">${escapeHtml(
    chapter?.title
  )}</h2>${chapter?.html || ""}</section>`;
}

function textOfHtml(html) {
  return foldReadingText(
    String(html || "")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|h[1-6]|li|blockquote|section|article|tr|figcaption|td|th|address)>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;|&#160;/gi, " ")
      .replace(/&#(\d+);/g, (_, value) => {
        const code = Number(value);
        return code > 0 && code < 0x110000 ? String.fromCodePoint(code) : "";
      })
      .replace(/&#x([0-9a-f]+);/gi, (_, value) => {
        const code = Number.parseInt(value, 16);
        return code > 0 && code < 0x110000 ? String.fromCodePoint(code) : "";
      })
      .replace(/&rsquo;|&#8217;/gi, "'")
      .replace(/&lsquo;|&#8216;/gi, "'")
      .replace(/&rdquo;|&#8221;/gi, '"')
      .replace(/&ldquo;|&#8220;/gi, '"')
      .replace(/&mdash;|&#8212;/gi, "-")
      .replace(/&ndash;|&#8211;/gi, "-")
      .replace(/&hellip;|&#8230;/gi, "…")
      .replace(/&oelig;/gi, "œ")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&apos;|&#39;/gi, "'")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
  );
}

function findAnchorChapter(chapters, anchor) {
  const folded = foldReadingText(anchor).slice(0, 120);
  if (folded.length < 8) return -1;
  const texts = chapters.map((chapter) => textOfHtml(chapter.html));
  const lengths = [folded.length, 80, 40].filter((length, index, list) => length >= 12 && list.indexOf(length) === index);
  for (const length of lengths) {
    const needle = folded.slice(0, length);
    const found = texts.findIndex((text) => text.includes(needle));
    if (found >= 0) return found;
  }
  const compact = folded.replace(/ /g, "");
  if (compact.length >= 12) {
    const needle = compact.slice(0, 80);
    const found = texts.findIndex((text) => text.replace(/ /g, "").includes(needle));
    if (found >= 0) return found;
  }
  return -1;
}

function chapterAtPercent(chapters, percent) {
  const sizes = chapters.map((chapter) => textOfHtml(chapter.html).length + 1);
  const total = sizes.reduce((sum, size) => sum + size, 0);
  let left = (Math.min(100, Math.max(0, percent)) / 100) * total;
  for (let index = 0; index < sizes.length; index += 1) {
    if (left <= sizes[index] || index === sizes.length - 1) {
      return { chapter: index, chapterFraction: Math.min(1, Math.max(0, left / sizes[index])) };
    }
    left -= sizes[index];
  }
  return { chapter: 0, chapterFraction: null };
}

/** Chapitre où rouvrir le livre, et repli dans ce chapitre si l'ancre n'y est pas retrouvée. */
function readingStart(chapters, place) {
  if (!place || !chapters.length) return { chapter: 0, chapterFraction: null };
  if (Number.isInteger(place.chapter) && place.chapter < chapters.length) {
    return { chapter: place.chapter, chapterFraction: place.chapterFraction };
  }
  const found = findAnchorChapter(chapters, place.anchor);
  if (found >= 0) return { chapter: found, chapterFraction: null };
  if (place.percent > 0) return chapterAtPercent(chapters, place.percent);
  return { chapter: 0, chapterFraction: null };
}

const ReadingFlow = memo(function ReadingFlow({ html, highlights, flowRef }) {
  const writtenHtml = useRef("");
  useLayoutEffect(() => {
    const node = flowRef.current;
    if (!node) return;
    if (writtenHtml.current !== html) {
      node.innerHTML = html || "";
      writtenHtml.current = html;
    }
    applyHighlightsToPages([node], highlights);
  }, [flowRef, highlights, html]);

  return (
    <div ref={flowRef} className="library-spread__flow library-epub" />
  );
});

function highlightAnchor(item) {
  const quote = String(item?.quote || "").replace(/\s+/g, " ").trim();
  const before = String(item?.before || "").replace(/\s+/g, " ").trim();
  const after = String(item?.after || "").replace(/\s+/g, " ").trim();
  if (quote.length >= 24) return quote.slice(0, 120);
  return `${before.slice(-48)} ${quote} ${after.slice(0, 48)}`.replace(/\s+/g, " ").trim().slice(0, 120);
}

function BookSpread({
  bookId,
  fontSize = 18,
  seekPage = null,
  onSeekApplied = () => {},
  onProgress = () => {},
  markTool = null,
  charactersToken = 0,
  searchToken = 0,
}) {
  const frameRef = useRef(null);
  const flowRef = useRef(null);
  const shiftRef = useRef(null);
  const columnsRef = useRef(null);
  const strideRef = useRef(0);
  const columnWidthRef = useRef(0);
  const spreadRef = useRef(0);
  const intentRef = useRef("anchor");
  const localPageRef = useRef(1);
  const chapterIndexRef = useRef(0);
  const navRef = useRef({ chapterIndex: 0, chapterCount: 0, localCount: 0 });
  const markToolRef = useRef(markTool);
  const highlightsRef = useRef([]);
  const fontWatchRef = useRef(fontSize);
  const movedRef = useRef(false);
  const restoreRef = useRef(null);
  const [chapters, setChapters] = useState([]);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [counts, setCounts] = useState([]);
  const [box, setBox] = useState(null);
  const [htmlBookId, setHtmlBookId] = useState(bookId);
  const [spread, setSpread] = useState(0);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const [marksReady, setMarksReady] = useState(false);
  const [markError, setMarkError] = useState("");
  if (htmlBookId !== bookId) {
    setHtmlBookId(bookId);
    setChapters([]);
    setChapterIndex(0);
    setCounts([]);
    setBox(null);
    setOpening(true);
    setSpread(0);
    setError("");
    setHighlights([]);
    setMarksReady(false);
    setMarkError("");
    intentRef.current = "anchor";
    movedRef.current = false;
    restoreRef.current = null;
    fontWatchRef.current = fontSize;
  } else if (fontWatchRef.current !== fontSize) {
    fontWatchRef.current = fontSize;
    intentRef.current = "anchor";
    setCounts((current) => current.map(() => 0));
  }
  const chapter = chapters[chapterIndex] || null;
  const html = chapter ? chapterMarkup(chapter) : "";
  const localCount = counts[chapterIndex] || 0;
  const offset = counts.slice(0, chapterIndex).reduce((sum, count) => sum + (count || 0), 0);
  const offsetReady = counts.slice(0, chapterIndex).every((count) => count > 0);
  const total = counts.reduce((sum, count) => sum + (count || 0), 0);
  const totalKnown = chapters.length > 0 && counts.length === chapters.length && counts.every((count) => count > 0);
  chapterIndexRef.current = chapterIndex;
  spreadRef.current = spread;
  navRef.current = { chapterIndex, chapterCount: chapters.length, localCount };
  markToolRef.current = markTool;
  highlightsRef.current = highlights;

  useEffect(() => {
    if (!bookId) return undefined;
    let cancelled = false;
    setOpening(true);
    setError("");
    fetchLibraryReading(bookId)
      .then((json) => {
        if (cancelled) return;
        const chapters = Array.isArray(json.chapters) ? json.chapters : [];
        if (!chapters.length) {
          setError("Ce livre n'a pas de chapitre lisible.");
          setOpening(false);
          return;
        }
        const prepared = chapters.map((chapter) => {
          const reading = takeEpubCss(chapter.html || "");
          return { title: chapter.title || "", html: reading.html, css: reading.css };
        });
        const seen = new Set();
        const css = [];
        prepared.forEach((chapter) => {
          if (chapter.css && !seen.has(chapter.css)) {
            seen.add(chapter.css);
            css.push(chapter.css);
          }
        });
        mountEpubCss(css.join("\n"));
        const stored = prepared.map(({ title, html: chapterHtml }) => ({ title, html: chapterHtml }));
        const start = readingStart(stored, readLibraryPlace(bookId));
        intentRef.current = "anchor";
        restoreRef.current = start;
        setChapters(stored);
        setChapterIndex(start.chapter);
        setCounts(stored.map(() => 0));
        setSpread(0);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Impossible de lire le livre.");
          setOpening(false);
        }
      });
    return () => {
      cancelled = true;
      mountEpubCss("");
    };
  }, [bookId]);

  useEffect(() => {
    if (!bookId) return undefined;
    let cancelled = false;
    setHighlights([]);
    setMarksReady(false);
    setMarkError("");
    fetchLibraryHighlights(bookId)
      .then((json) => {
        if (cancelled) return;
        const loaded = Array.isArray(json.highlights) ? json.highlights : [];
        const partials = loaded.filter((item) => item.kind === "word" && isPartialWordQuote(item.after));
        partials.forEach((item) => {
          deleteLibraryHighlight(bookId, item.id).catch(() => {});
        });
        setHighlights(loaded.filter((item) => !partials.includes(item)));
      })
      .catch((err) => {
        if (!cancelled) setMarkError(err.message || "Impossible de lire les surlignages.");
      })
      .finally(() => {
        if (!cancelled) setMarksReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const showRemove = (event) => {
      const mark = event.target.closest?.(".library-mark");
      frame.querySelectorAll(".library-mark.is-hot").forEach((item) => item.classList.remove("is-hot"));
      if (!mark || !frame.contains(mark)) return;
      const id = mark.dataset.highlightId;
      frame.querySelectorAll(".library-mark[data-end='1']").forEach((item) => {
        if (item.dataset.highlightId === id) item.classList.add("is-hot");
      });
    };
    const hideRemove = (event) => {
      const next = event.relatedTarget;
      if (next && frame.contains(next) && next.closest?.(".library-mark")) return;
      frame.querySelectorAll(".library-mark.is-hot").forEach((item) => item.classList.remove("is-hot"));
    };
    const onMouseDown = (event) => {
      if (event.target.closest?.(".library-mark__remove")) event.preventDefault();
    };
    const onClick = (event) => {
      const button = event.target.closest?.(".library-mark__remove");
      if (!button || !frame.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      const id = button.dataset.highlightId;
      if (!id) return;
      const previous = highlightsRef.current;
      setHighlights((current) => current.filter((item) => item.id !== id));
      if (String(id).startsWith("pending-")) return;
      deleteLibraryHighlight(bookId, id).catch((err) => {
        setHighlights(previous);
        setMarkError(err.message || "Impossible de retirer le surlignage.");
      });
    };
    frame.addEventListener("mouseover", showRemove);
    frame.addEventListener("mouseout", hideRemove);
    frame.addEventListener("mousedown", onMouseDown);
    frame.addEventListener("click", onClick);
    return () => {
      frame.removeEventListener("mouseover", showRemove);
      frame.removeEventListener("mouseout", hideRemove);
      frame.removeEventListener("mousedown", onMouseDown);
      frame.removeEventListener("click", onClick);
    };
  }, [bookId]);

  useEffect(() => {
    const onUp = (event) => {
      const tool = markToolRef.current;
      if (!tool) return;
      if (event.target?.closest?.(".library-mark-tool, .library-mark__remove, .library-menu-close")) return;
      const pagesRoot = frameRef.current?.querySelector(".library-spread__flow");
      if (!pagesRoot) return;
      const selection = window.getSelection();
      const range = selection && selection.rangeCount === 1 ? selection.getRangeAt(0) : null;
      const record = readSelection(pagesRoot);
      if (!record) return;
      const already = range && pagesRoot.contains(range.startContainer) && selectionTouchesMark(range);
      window.getSelection()?.removeAllRanges();
      if (already) {
        setMarkError("Cet endroit est déjà surligné.");
        return;
      }
      const tempId = `pending-${Date.now()}`;
      const mark = { kind: tool, quote: record.quote, before: record.before, after: record.after };
      setHighlights((current) => [...current, { id: tempId, ...mark }]);
      createLibraryHighlight(bookId, mark)
        .then((saved) => {
          setHighlights((current) => current.map((item) => (item.id === tempId ? saved : item)));
          setMarkError("");
        })
        .catch((err) => {
          setHighlights((current) => current.filter((item) => item.id !== tempId));
          setMarkError(err.message || "Impossible d'enregistrer le surlignage.");
        });
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, [bookId]);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const flow = flowRef.current;
    const shift = shiftRef.current;
    if (!frame || !html || !flow || !shift || !marksReady) return undefined;
    let locked = null;

    const applyScale = (pageBox, nextWidth, nextHeight) => {
      const scale = Math.min(1, nextWidth / pageBox.width, nextHeight / pageBox.height);
      frame.style.setProperty("--library-spread-w", `${pageBox.width}px`);
      frame.style.setProperty("--library-spread-h", `${pageBox.height}px`);
      frame.style.setProperty(
        "--library-read-scale",
        String(Number.isFinite(scale) && scale > 0 ? scale : 1)
      );
    };

    const layout = () => {
      const nextWidth = frame.clientWidth;
      const nextHeight = frame.clientHeight;
      if (nextWidth < 80 || nextHeight < 80) return;
      if (!locked) locked = { width: nextWidth, height: nextHeight };
      applyScale(locked, nextWidth, nextHeight);
      const columnWidth = (locked.width - COLUMN_GAP) / 2;
      if (columnWidth < 40) return;
      flow.style.columnWidth = `${columnWidth}px`;
      flow.style.height = `${Math.max(40, locked.height - PAGE_NUMBER_BAND)}px`;
      flow.style.fontSize = `${fontSize}px`;
      flow.style.hyphens = "manual";
      shift.style.transform = "translateX(0px)";
      const count = pageCountFromFlow(flow, columnWidth);
      writeStoredPageCount(
        chapterCountKey(bookId, fontSize, locked.width, locked.height, chapterIndex),
        count
      );
      columnWidthRef.current = columnWidth;
      strideRef.current = columnStride(columnWidth);
      const spreads = Math.max(1, Math.ceil(count / 2));
      const intent = intentRef.current;
      let nextSpread = 0;
      if (intent === "end") nextSpread = spreads - 1;
      else if (intent === "start") nextSpread = 0;
      else if (intent === "page") {
        nextSpread = Math.min(spreads - 1, Math.max(0, Math.floor((localPageRef.current - 1) / 2)));
      } else if (intent === "anchor") {
        const place = readLibraryPlace(bookId);
        const found = spreadForAnchor(flow, place?.anchor || "", columnWidth, count, columnsRef.current, shift);
        const hint = restoreRef.current || place;
        restoreRef.current = null;
        const fraction = hint?.chapter === chapterIndex ? hint.chapterFraction : null;
        if (found != null) nextSpread = Math.min(spreads - 1, found);
        else if (Number.isFinite(fraction)) {
          nextSpread = Math.min(spreads - 1, Math.max(0, Math.floor(Math.round(fraction * count) / 2)));
        }
      } else {
        nextSpread = Math.min(spreads - 1, Math.max(0, spreadRef.current));
      }
      intentRef.current = "keep";
      spreadRef.current = nextSpread;
      setBox((current) =>
        current && current.width === locked.width && current.height === locked.height
          ? current
          : { width: locked.width, height: locked.height }
      );
      setCounts((current) => {
        if (current[chapterIndex] === count) return current;
        const next = current.slice();
        next[chapterIndex] = count;
        return next;
      });
      setSpread(nextSpread);
      setOpening(false);
      shift.style.transform = `translateX(${-nextSpread * 2 * strideRef.current}px)`;
    };

    layout();
    const onResize = () => {
      const nextWidth = frame.clientWidth;
      const nextHeight = frame.clientHeight;
      if (nextWidth < 80 || nextHeight < 80) return;
      if (!locked) {
        layout();
        return;
      }
      applyScale(locked, nextWidth, nextHeight);
    };
    const observer = new ResizeObserver(onResize);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [bookId, chapterIndex, fontSize, html, marksReady]);

  useEffect(() => {
    if (!box || !chapters.length) return undefined;
    let cancelled = false;
    const columnWidth = (box.width - COLUMN_GAP) / 2;
    if (columnWidth < 40) return undefined;
    const cached = [];
    const missing = [];
    chapters.forEach((_, index) => {
      const key = chapterCountKey(bookId, fontSize, box.width, box.height, index);
      const count = readStoredPageCount(key);
      if (count) cached.push({ index, count });
      else missing.push({ index, key });
    });
    if (cached.length) {
      setCounts((current) => {
        const next = current.slice();
        let changed = false;
        cached.forEach((item) => {
          if (next[item.index] === item.count) return;
          next[item.index] = item.count;
          changed = true;
        });
        return changed ? next : current;
      });
    }
    if (!missing.length) return undefined;
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
      "position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none;";
    const gauge = document.createElement("div");
    gauge.className = "library-spread__flow library-epub";
    gauge.style.columnWidth = `${columnWidth}px`;
    gauge.style.height = `${Math.max(40, box.height - PAGE_NUMBER_BAND)}px`;
    gauge.style.width = `${box.width}px`;
    gauge.style.fontSize = `${fontSize}px`;
    host.append(gauge);
    document.body.append(host);
    let cursor = 0;
    let handle = 0;
    const idle = typeof window.requestIdleCallback === "function";
    const schedule = () => {
      handle = idle ? window.requestIdleCallback(step, { timeout: 800 }) : window.setTimeout(step, 32);
    };
    const step = () => {
      if (cancelled) return;
      if (cursor >= missing.length) {
        host.remove();
        return;
      }
      const item = missing[cursor];
      cursor += 1;
      gauge.innerHTML = chapterMarkup(chapters[item.index]);
      const count = pageCountFromFlow(gauge, columnWidth);
      gauge.innerHTML = "";
      writeStoredPageCount(item.key, count);
      if (!cancelled) {
        setCounts((current) => {
          if (current[item.index] === count) return current;
          const next = current.slice();
          next[item.index] = count;
          return next;
        });
      }
      if (!cancelled) schedule();
    };
    schedule();
    return () => {
      cancelled = true;
      if (idle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
      host.remove();
    };
  }, [bookId, box, chapters, fontSize]);

  useEffect(() => {
    if (!error) return;
    onProgress({ page: 1, total: 0, ready: true, shown: true });
  }, [error, onProgress]);

  useEffect(() => {
    if (opening || !localCount) {
      if (!opening && !totalKnown) onProgress({ page: 0, total: 0, ready: false, shown: false });
      return;
    }
    const page = offset + spread * 2 + 1;
    if (totalKnown) {
      onProgress({
        page,
        total,
        ready: true,
        shown: true,
      });
    } else {
      onProgress({ page: 0, total: 0, ready: false, shown: false });
    }
    const moved = movedRef.current;
    if (!totalKnown && !moved) return;
    const flow = flowRef.current;
    const columnWidth = columnWidthRef.current;
    let next;
    if (moved && flow && columnWidth) {
      const leftPage = spreadRef.current * 2;
      next = {
        anchor: pageAnchor(flow, leftPage, columnWidth),
        chapter: chapterIndexRef.current,
        chapterFraction: leftPage / localCount,
      };
    } else {
      const seen = readLibraryPlace(bookId);
      if (!seen?.anchor) return;
      next = { anchor: seen.anchor, chapter: seen.chapter, chapterFraction: seen.chapterFraction };
    }
    writeLibraryPlace(bookId, {
      ...next,
      fontSize,
      percent: totalKnown && total ? Math.round((page / total) * 100) : undefined,
    });
  }, [bookId, fontSize, localCount, offset, onProgress, opening, spread, total, totalKnown]);

  useEffect(() => {
    if (!seekPage || !totalKnown) return;
    movedRef.current = true;
    let page = Math.min(total, Math.max(1, seekPage));
    let index = 0;
    while (index < counts.length - 1 && page > counts[index]) {
      page -= counts[index];
      index += 1;
    }
    if (index !== chapterIndex) {
      localPageRef.current = page;
      intentRef.current = "page";
      setChapterIndex(index);
    } else {
      const spreads = Math.max(1, Math.ceil((counts[index] || 1) / 2));
      setSpread(Math.min(spreads - 1, Math.floor((page - 1) / 2)));
    }
    onSeekApplied();
  }, [chapterIndex, counts, onSeekApplied, seekPage, total, totalKnown]);

  useEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;
    let lockedUntil = 0;
    const turn = (direction) => {
      const now = Date.now();
      if (now < lockedUntil) return;
      const nav = navRef.current;
      if (!nav.localCount) return;
      movedRef.current = true;
      lockedUntil = now + 280;
      const spreadCount = Math.max(1, Math.ceil(nav.localCount / 2));
      setSpread((current) => {
        const next = current + direction;
        if (next >= spreadCount) {
          if (nav.chapterIndex < nav.chapterCount - 1) {
            intentRef.current = "start";
            setChapterIndex(nav.chapterIndex + 1);
          }
          return current;
        }
        if (next < 0) {
          if (nav.chapterIndex > 0) {
            intentRef.current = "end";
            setChapterIndex(nav.chapterIndex - 1);
          }
          return current;
        }
        return next;
      });
    };
    const onKey = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.target?.isContentEditable) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && node.contains(selection.anchorNode)) return;
      event.preventDefault();
      turn(event.key === "ArrowLeft" ? -1 : 1);
    };
    const onWheel = (event) => {
      if (!event.deltaY) return;
      if (
        event.target?.closest?.(
          ".library-characters__window, .library-characters__menu, .pack-assign-dropdown__menu, .library-reading-preferences"
        )
      ) {
        return;
      }
      if (document.querySelector(".kingdom-mail__window")) return;
      event.preventDefault();
      turn(event.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onWheel);
    };
  }, [bookId]);

  useLayoutEffect(() => {
    const shift = shiftRef.current;
    if (!shift || !strideRef.current) return;
    shift.style.transform = `translateX(${-spreadRef.current * 2 * strideRef.current}px)`;
  }, [chapterIndex, spread]);

  const goToAnchor = (anchor, chapterHint) => {
    if (!anchor || !chapters.length) return;
    const place = readLibraryPlace(bookId);
    const known =
      Number.isInteger(chapterHint) && chapterHint >= 0 && chapterHint < chapters.length
        ? chapterHint
        : findAnchorChapter(chapters, anchor);
    writeLibraryPlace(bookId, {
      anchor,
      fontSize,
      percent: place?.percent,
      chapter: known >= 0 ? known : undefined,
    });
    const index = Math.max(0, known);
    movedRef.current = true;
    if (index !== chapterIndexRef.current) {
      intentRef.current = "anchor";
      setChapterIndex(index);
      return;
    }
    const flow = flowRef.current;
    const count = counts[index] || 0;
    const columnWidth = columnWidthRef.current;
    if (!flow || !columnWidth || !count) {
      intentRef.current = "anchor";
      setChapterIndex(index);
      return;
    }
    const found = spreadForAnchor(
      flow,
      anchor,
      columnWidth,
      count,
      columnsRef.current,
      shiftRef.current
    );
    if (found == null) return;
    const spreads = Math.max(1, Math.ceil(count / 2));
    const next = Math.min(spreads - 1, Math.max(0, found));
    spreadRef.current = next;
    setSpread(next);
    if (shiftRef.current && strideRef.current) {
      shiftRef.current.style.transform = `translateX(${-next * 2 * strideRef.current}px)`;
    }
  };

  const showHighlight = (highlight) => goToAnchor(highlightAnchor(highlight));
  const showSearchHit = (hit) => goToAnchor(hit?.anchor, hit?.chapterIndex);

  const leftNumber = localCount && offsetReady ? offset + spread * 2 + 1 : 0;
  const rightLocal = spread * 2 + 2;
  const rightNumber = localCount && offsetReady && rightLocal <= localCount ? offset + rightLocal : 0;

  return (
    <div className="library-spread">
      <div className="library-spread__frame" ref={frameRef}>
        <div className="library-spread__fit">
          <div className="library-spread__fit-inner">
            {html && !error ? (
              <div
                className="library-spread__columns"
                ref={columnsRef}
                style={{ visibility: opening ? "hidden" : "visible" }}
              >
                <div className="library-spread__shift" ref={shiftRef}>
                  <ReadingFlow html={html} highlights={highlights} flowRef={flowRef} />
                </div>
                {leftNumber ? <span className="library-spread__num library-spread__num--left">{leftNumber}</span> : null}
                {rightNumber ? <span className="library-spread__num library-spread__num--right">{rightNumber}</span> : null}
              </div>
            ) : null}
            {opening && !error ? (
              <div className="library-spread__pages library-spread__pages--skel" aria-hidden="true">
                <div className="library-spread__leaf">
                  <OpeningPage fontSize={fontSize} />
                </div>
                <div className="library-spread__leaf">
                  <OpeningPage fontSize={fontSize} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {error ? <p className="libraryView__empty">{error}</p> : null}
        {markError ? <p className="library-spread__mark-error">{markError}</p> : null}
      </div>
      <LibraryCharacters
        bookId={bookId}
        frameRef={frameRef}
        highlights={highlights}
        marksReady={marksReady}
        openToken={charactersToken}
        onShowPassage={showHighlight}
      />
      <LibrarySearch
        bookId={bookId}
        chapters={chapters}
        openToken={searchToken}
        onShowHit={showSearchHit}
      />
    </div>
  );
}

const SHELF_PAGE_SIZE = 14;
const SHELF_SKELETON_COUNT = 14;

const OPENING_LOREM =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.";

function OpeningPage({ fontSize }) {
  return (
    <article className="library-spread__page library-spread__page--skel" style={{ fontSize: `${fontSize}px` }}>
      {Array.from({ length: 8 }, (_, index) => (
        <p key={index}>{OPENING_LOREM}</p>
      ))}
    </article>
  );
}

function ShelfSkeleton() {
  return (
    <ul className="library-shelf library-shelf--skel" aria-hidden="true">
      {Array.from({ length: SHELF_SKELETON_COUNT }, (_, index) => (
        <li key={index}>
          <div className="library-shelf__book">
            <div className="library-shelf__cover">
              <span className="register-skel library-shelf-skel__cover" />
            </div>
            <div className="library-shelf__meta library-shelf-skel__meta">
              <span className="library-shelf__title">
                <span
                  className="register-skel library-shelf-skel__bar"
                  style={{ width: index % 2 === 0 ? "100%" : "calc(100% - 10px)" }}
                />
              </span>
              <span className="library-shelf__author">
                <span
                  className="register-skel library-shelf-skel__bar"
                  style={{ width: index % 2 === 0 ? "calc(100% - 10px)" : "100%" }}
                />
              </span>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function shelfPageMarks(current, count) {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index);
  const indexes = [0, count - 1, current - 1, current, current + 1].filter(
    (index, place, list) => index >= 0 && index < count && list.indexOf(index) === place
  );
  indexes.sort((a, b) => a - b);
  const marks = [];
  indexes.forEach((index, place) => {
    if (place > 0 && index - indexes[place - 1] > 1) marks.push(`gap-${index}`);
    marks.push(index);
  });
  return marks;
}

export default function LibraryView({
  query = "",
  searchScope = "title",
  openedBookId = null,
  onOpenBook = () => {},
  onCaptureWord = null,
  fontSize = 18,
  seekPage = null,
  onSeekApplied = () => {},
  onProgress = () => {},
  reloadToken = 0,
  favoritesOnly = false,
  onToggleFavorites = () => {},
  markTool = null,
  charactersToken = 0,
  searchToken = 0,
  highlightsOpen = false,
  highlightQuery = "",
  highlightResume = null,
  onHighlightResumeApplied = () => {},
  onHighlightKindChange = () => {},
  onCloseHighlights = () => {},
  onResumeBookChange = () => {},
}) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [favoriteIds, setFavoriteIds] = useState(() => readLibraryFavorites());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [readingFilter, setReadingFilter] = useState("all");
  const shelfRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (reloadToken === 0) setLoading(true);
    setError("");
    fetchLibraryBooks()
      .then((json) => {
        if (cancelled) return;
        setBooks(Array.isArray(json.books) ? json.books : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Impossible de lire la bibliothèque.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    if (loading) return;
    const id = latestInProgressBookId(books.map((book) => book.id));
    const match = id ? books.find((book) => book.id === id) : null;
    onResumeBookChange(
      match
        ? { id: match.id, title: String(match.title || "").trim() || "Sans titre" }
        : null
    );
  }, [books, loading, openedBookId, onResumeBookChange]);

  const normalizedQuery = normalizeRegisterSearch(query);
  const visibleBooks = books.filter((item) => {
    if (favoritesOnly && !favoriteIds.includes(item.id)) return false;
    if (readingFilter !== "all" && readingStatus(item.id) !== readingFilter) return false;
    return bookMatches(item, normalizedQuery, searchScope);
  });
  const pageCount = Math.max(1, Math.ceil(visibleBooks.length / SHELF_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);

  useEffect(() => {
    setPage(0);
  }, [query, favoritesOnly, readingFilter, searchScope]);

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  const shelfPageRef = useRef(currentPage);
  const shelfPageCountRef = useRef(pageCount);
  shelfPageRef.current = currentPage;
  shelfPageCountRef.current = pageCount;

  useEffect(() => {
    if (openedBookId || highlightsOpen) return undefined;
    const root = shelfRef.current;
    if (!root) return undefined;
    let lockedUntil = 0;
    const onWheel = (event) => {
      if (event.ctrlKey) return;
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (Math.abs(delta) < 4) return;
      const count = shelfPageCountRef.current;
      if (count <= 1) return;
      event.preventDefault();
      const now = performance.now();
      if (now < lockedUntil) return;
      const direction = delta > 0 ? 1 : -1;
      const current = shelfPageRef.current;
      const next = Math.min(count - 1, Math.max(0, current + direction));
      if (next === current) return;
      lockedUntil = now + 420;
      setPage(next);
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [openedBookId, highlightsOpen, loading]);

  const catalogCharacters = (
    <LibraryCharacters
      pickBook
      openToken={charactersToken}
      onOpenBook={onOpenBook}
    />
  );

  if (openedBookId) {
    return (
      <div className="libraryView libraryView--read">
        <BookSpread
          bookId={openedBookId}
          fontSize={fontSize}
          seekPage={seekPage}
          onSeekApplied={onSeekApplied}
          onProgress={onProgress}
          markTool={markTool}
          charactersToken={charactersToken}
          searchToken={searchToken}
        />
      </div>
    );
  }

  if (highlightsOpen) {
    return (
      <div className="libraryView libraryView--highlights">
        <LibraryHighlightsView
          books={books}
          loadingBooks={loading}
          query={highlightQuery}
          resume={highlightResume}
          onResumeApplied={onHighlightResumeApplied}
          onKindChange={onHighlightKindChange}
          onClose={onCloseHighlights}
          onOpenBook={onOpenBook}
          onCaptureWord={onCaptureWord}
        />
        {catalogCharacters}
      </div>
    );
  }

  const emptyLabel = favoritesOnly
    ? "Aucun livre en favori."
    : normalizedQuery || readingFilter !== "all"
      ? "Aucun livre ne correspond."
      : "Aucun livre.";

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteLibraryBook(pendingDelete.id);
      forgetCover(pendingDelete.id);
      if (favoriteIds.includes(pendingDelete.id)) {
        setFavoriteIds(toggleLibraryFavorite(pendingDelete.id));
      }
      setBooks((current) => current.filter((item) => item.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setError(err.message || "Impossible de retirer le livre.");
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="libraryView" ref={shelfRef}>
      <header className="libraryView__head libraryView__head--shelf">
        <div className="library-mode-switch">
          <span>Tous</span>
          <ToggleSwitch
            plain
            compact
            checked={favoritesOnly}
            onChange={onToggleFavorites}
            trackOnColor="#39210d"
            trackOffColor="#39210d"
            ariaLabel="Afficher seulement les livres favoris"
            className="library-mode-switch__toggle"
          />
          <span>livres favoris</span>
        </div>
        <h1 className="libraryView__title">
          {loading ? "Votre bibliothèque" : `Votre bibliothèque (${books.length})`}
        </h1>
        <div className="library-status-filter">
          <PackAssignDropdown
            options={LIBRARY_STATUS_OPTIONS}
            value={readingFilter}
            onChange={setReadingFilter}
            ariaLabel="Filtrer les livres selon la lecture"
            matchTriggerWidth
            openBelow
            menuMaxHeight={220}
          />
        </div>
      </header>
      {error ? <p className="libraryView__empty">{error}</p> : null}
      {loading ? <ShelfSkeleton /> : null}
      {!loading && !error && visibleBooks.length ? (
        <ul className="library-shelf">
          {visibleBooks
            .slice(currentPage * SHELF_PAGE_SIZE, (currentPage + 1) * SHELF_PAGE_SIZE)
            .map((item) => {
            const percent = readLibraryPlace(item.id)?.percent || 0;
            const favorite = favoriteIds.includes(item.id);
            return (
              <li key={item.id}>
                <div className="library-shelf__book">
                  <div className="library-shelf__cover">
                    <button
                      type="button"
                      className="library-shelf__open"
                      onClick={() => onOpenBook(item.id)}
                      aria-label={item.title}
                    >
                      <BookCover bookId={item.id} hasCover={item.hasCover} />
                    </button>
                    <span className="library-shelf__progress">
                      <span
                        className="library-shelf__progress-fill"
                        style={{ width: `${percent}%` }}
                      />
                      <span className="library-shelf__progress-label">{percent}%</span>
                    </span>
                    <div className="library-shelf__hover">
                      <button
                        type="button"
                        className="library-shelf__hover-btn"
                        aria-label={favorite ? "Retirer des favoris" : "Mettre en favori"}
                        aria-pressed={favorite}
                        onClick={() => setFavoriteIds(toggleLibraryFavorite(item.id))}
                      >
                        <Heart
                          size={28}
                          strokeWidth={1.75}
                          fill={favorite ? "currentColor" : "none"}
                        />
                      </button>
                      {item.mine ? (
                        <button
                          type="button"
                          className="library-shelf__hover-btn"
                          aria-label="Retirer ce livre"
                          onClick={() => setPendingDelete(item)}
                        >
                          <Trash2 size={28} strokeWidth={1.75} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="library-shelf__meta"
                    onClick={() => onOpenBook(item.id)}
                  >
                    <span className="library-shelf__title">{item.title}</span>
                    <span className="library-shelf__author">{item.author || "\u00a0"}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      {!loading && !error && visibleBooks.length ? (
        <nav className="library-pager" aria-label="Pages de la bibliothèque">
          <div className="library-pager__controls">
            <button
              type="button"
              className="library-pager__arrow"
              aria-label="Page précédente"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft size={20} color="#39210d" strokeWidth={1.25} />
            </button>
            {shelfPageMarks(currentPage, pageCount).map((mark) =>
              typeof mark === "number" ? (
                <button
                  key={mark}
                  type="button"
                  className={
                    mark === currentPage
                      ? "library-pager__page library-pager__page--current"
                      : "library-pager__page"
                  }
                  aria-label={`Page ${mark + 1}`}
                  aria-current={mark === currentPage ? "page" : undefined}
                  onClick={() => setPage(mark)}
                >
                  {mark + 1}
                </button>
              ) : (
                <span key={mark} className="library-pager__gap" aria-hidden="true">
                  ·
                </span>
              )
            )}
            <button
              type="button"
              className="library-pager__arrow"
              aria-label="Page suivante"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              <ChevronRight size={20} color="#39210d" strokeWidth={1.25} />
            </button>
          </div>
        </nav>
      ) : null}
      {!loading && !error && !visibleBooks.length ? (
        <p className="libraryView__empty">{emptyLabel}</p>
      ) : null}
      <NotesConfirmModal
        open={Boolean(pendingDelete)}
        title="Retirer ce livre"
        message={
          pendingDelete
            ? `« ${pendingDelete.title} » sera retiré de votre bibliothèque.`
            : ""
        }
        confirmLabel={deleting ? "Retrait…" : "Retirer"}
        cancelLabel="Annuler"
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
      {catalogCharacters}
    </div>
  );
}
